import { useRef, useEffect } from 'react';
import { Button, message } from 'antd';
import Script from 'next/script';
import { useCountDown, useLockFn } from 'ahooks';
import { assetsPath, initGeetestCaptcha } from './utils';
import { sendSMSEmailRequest, sendSMSRequest } from '@/service/user';
// import verify from '@/utils/verify';
import styles from './index.module.less';
import classNames from 'classnames';
import { useState } from 'react';
import { track } from '@/utils/track';

type SendBtnType = 'mobile' | 'email';

/**
 * 注意
 * 440 code码 请求频繁
 */

export enum OperationTypeMap {
  REGISTER = 'textin_register',
  LOGIN = 'textin_login',
  SET_PWD = 'textin_set_password',
  AUTH = 'textin_create_credential',
  BIND_MOBILE = 'textin_bind_mobile',
  BIND_EMAIL = 'textin_bind_email',
}
type BeforeVerify = () => boolean;
type BeforeVerifyPromise = () => Promise<void>;

interface IProps {
  value: string | (() => string);
  operationType: OperationTypeMap | string;
  // 验证类型 mobile、email
  type?: SendBtnType;
  // 按钮文案
  text?: string;
  // 按钮参数 ButtonProps
  props?: any;
  errCallBack?: (msg: string) => void;
  beforeVerify: BeforeVerify | BeforeVerifyPromise;
  className?: string;
}

/**
 * @description 发送验证码组件
 * @TODO: callback 处理
 */

const GeetestCaptcha = ({
  value,
  props = {},
  errCallBack,
  operationType,
  text = '获取验证码',
  type = 'mobile',
  beforeVerify,
  className,
}: IProps) => {
  const [countdown, setTargetDate] = useCountDown();
  const geetestRef = useRef<any>();
  const [btnLoading, setBtnLoading] = useState(props.loading || false);
  const valueRef = useRef('');
  valueRef.current = typeof value === 'function' ? value() : value;

  const initData = async () => {
    try {
      const { captchaObj, challenge } = await initGeetestCaptcha();

      captchaObj.onSuccess(() => {
        // 成功后极速验证重置
        captchaObj.reset();
        setTargetDate(Date.now() + 59000);
        // 验证完成 发送验证码
        if (type === 'mobile') {
          sendMessage(challenge, captchaObj.getValidate());
        } else if (type === 'email') {
          sendEmail(challenge, captchaObj.getValidate());
        }
      });

      captchaObj.onError((error) => {
        message.warning('网络或资源加载失败，请刷新重试');
        track({ name: '极验错误', keyword: '验证出错', message: error });
      });

      await new Promise((resolve) => {
        const next = () => {
          geetestRef.current = captchaObj;
          resolve(true);
        };
        captchaObj.onReady(next);
        setTimeout(next, 500);
      });
    } catch (error: any) {
      message.error(error.msg || error.message);
    }
  };
  const geeVerify = async () => {
    try {
      const verifyFlag = beforeVerify();
      if (verifyFlag instanceof Promise) {
        await verifyFlag;
      } else {
        if (!beforeVerify()) {
          throw new Error('校验失败');
        }
      }
      try {
        geetestRef.current.verify();
      } catch (error) {
        console.log('geeVerify-error', error);
        track({ name: '极验错误', keyword: 'verify报错', message: error });
      }
    } catch (e) {
      console.log('geeVerify-error', e);
      track({ name: '验证码发送失败', keyword: valueRef.current, message: e });
    } finally {
      setBtnLoading(false);
    }
  };
  const handleClick = useLockFn(async () => {
    setBtnLoading(true);

    if (!geetestRef.current) {
      track({ name: '极验错误', keyword: '重新未初始化initData', message: valueRef.current });
      await initData();
    }

    if (!geetestRef.current) {
      setBtnLoading(false);
      message.warning('网络或资源加载失败，请刷新重试');
      track({ name: '极验错误', keyword: '未初始化', message: valueRef.current });
      return;
    }

    geeVerify();
  });

  const sendMessage = async (challenge: string, validateData: any) => {
    try {
      track({ name: '触发获取验证码', keyword: valueRef.current });
      const res = await sendSMSRequest({
        mobile: valueRef.current,
        oldChallenge: challenge,
        operationType,
        geetestChallenge: validateData.geetest_challenge,
        geetestValidate: validateData.geetest_validate,
        geetestSeccode: validateData.geetest_seccode,
        forceSend: 1,
      });
      const { data: { send_status, send_message } = {}, code, msg } = res;
      if (code === 200) {
        if (send_status !== 1) {
          message.error(send_message);
        }
      } else {
        track({ name: '验证码发送失败', keyword: valueRef.current, message: res });
        // 重置倒计时
        setTargetDate(undefined);

        /**
         * 403 code码
         * 登录页面 - 用户未被注册返回错误码
         * 注册页面 - 用户已被注册
         * 邮箱绑定功能 - 邮箱已被绑定
         */
        if (msg) {
          if (errCallBack) {
            errCallBack(msg);
            return;
          }
        }
        message.error(msg);
      }
    } catch (e) {
      track({ name: '验证码发送失败', keyword: valueRef.current, message: e });
      message.error((e as any).data.msg);
    }
  };

  const sendEmail = async (challenge: string, validateData: any) => {
    try {
      const {
        code,
        msg,
        data: { send_status, send_message } = {},
      } = await sendSMSEmailRequest({
        email: valueRef.current,
        oldChallenge: challenge,
        operationType,
        geetestChallenge: validateData.geetest_challenge,
        geetestValidate: validateData.geetest_validate,
        geetestSeccode: validateData.geetest_seccode,
      });
      if (code === 200) {
        if (send_status !== 1) {
          message.error(send_message);
        }
      } else {
        setTargetDate(undefined);
        /**
         * 403 code码
         * 登录页面 - 用户未被注册返回错误码
         * 注册页面 - 用户已被注册
         * 邮箱绑定功能 - 邮箱已被绑定
         */
        if (msg) {
          if (errCallBack) {
            errCallBack(msg);
            return;
          }
        }
        message.error(msg);
      }
    } catch (e) {
      message.error((e as any).data.msg);
    }
  };
  useEffect(() => {
    setBtnLoading(props.loading);
  }, [props.loading]);
  return (
    <>
      <Button
        onClick={handleClick}
        className={classNames(styles.btn, 'textin-radius-2', className)}
        disabled={countdown !== 0}
        {...props}
        loading={btnLoading}
      >
        {countdown > 0 && `${Math.round(countdown / 1000)}s后重新获取`}
        {countdown === 0 && text}
      </Button>
      <Script
        src={assetsPath}
        onReady={() => {
          console.log('initData geetest');
          initData();
        }}
        onError={(e) => {
          track({ name: '极验错误', keyword: 'cdn加载失败', message: e });
        }}
      />
    </>
  );
};

export default GeetestCaptcha;
